import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { checkoutSession } from '../../services/apiService';
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle.min.js';
import './ConfirmBookingPage.css'; 
import AuthCheck from '../../components/authCheck/AuthCheck';
import { createEventSlug } from '../../utils/urlUtils';

// Load Stripe publishable key from environment variables
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!stripePublishableKey) {
  console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not defined in environment variables');
}

const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

// Utility function to safely format dates
const formatEventDate = (datetime) => {
  try {
    const eventDate = new Date(datetime);
    if (isNaN(eventDate.getTime())) {
      return 'Date not available';
    }
    
    // Format date and time in a user-friendly way
    const dateStr = eventDate.toLocaleDateString();
    const timeStr = eventDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    return `${dateStr} at ${timeStr}`;
  } catch (error) {
    return 'Date not available';
  }
};

const PaymentForm = ({ confirmedBooking, eventName, onSuccess, onError }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!confirmedBooking) return;

    setProcessing(true);
    setError('');
    onError(''); // Clear parent error

    try {
      const { event, selectedSeats, grandTotal } = confirmedBooking;
      console.log('EventName:', eventName);
      console.log('Confirmed booking:', confirmedBooking);
      
      // Create checkout session directly
      const sessionData = {
        eventId: event._id || event.id,
          // seats: selectedSeats.map(s => `${s.row}-${s.number}`),
          seats: selectedSeats.map(seat => ({
            ...seat,
            seatId: seat.id,
            id: undefined
          })).map(({ id, ...seat }) => seat), // Remove the old id field
          totalPrice: grandTotal,
          paymentStatus: 'pending', // Will be updated after Stripe confirmation
          platformFeeAmount: 0,
        paymentDetails: {
         
          successUrl: `${window.location.origin}/payment-success`,
          cancelUrl: `${window.location.origin}/payment-error`,
        }
      };

      console.log('Session data being sent:', sessionData);

      const checkoutRes = await checkoutSession(sessionData);
      console.log('Full checkout response:', checkoutRes);
      console.log('Response data:', checkoutRes?.data);
      console.log('Payment result url:', checkoutRes?.paymentResult?.url);

      // Check if we got a successful response with the checkout URL
      if (checkoutRes?.paymentResult?.url) {
        console.log('✅ Redirecting to Stripe checkout:', checkoutRes.paymentResult.url);
        window.location.href = checkoutRes.paymentResult.url; // Redirect to Stripe Checkout
      } else {
        console.error('❌ Invalid response structure:', {
          status: checkoutRes?.paymentResult?.status,
          hasUrl: !!checkoutRes?.paymentResult?.url,
          fullResponse: checkoutRes
        });
        throw new Error('Stripe Checkout URL not returned or payment result not successful');
      }
    } catch (err) {
      console.error('❌ Payment error details:', {
        error: err,
        message: err?.message,
        response: err?.response?.data,
        status: err?.response?.status
      });
      
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to initiate payment. Please try again.';
      setError(errorMessage);
      onError(errorMessage);
      setProcessing(false);
    }
  };

  return (
    <div className="payment-section mt-4 p-3 border rounded bg-light">
      <h6 className="mb-3">Complete Your Payment</h6>
      <form onSubmit={handleSubmit}>
        <p className="text-muted small">
          You will be redirected to a secure Stripe checkout page to complete your payment.
        </p>
        {error && <div className="alert alert-danger small">{error}</div>}
        <button 
          type="submit" 
          className="w-100 btn btn-success" 
          disabled={processing}
        >
          {processing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Processing...
            </>
          ) : (
            'Complete Payment'
          )}
        </button>
      </form>
    </div>
  );
};

const ConfirmBookingPage = () => {
  const navigate = useNavigate();
  const { eventName } = useParams();
  const [bookingData, setBookingData] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [paymentError, setPaymentError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const stored = sessionStorage.getItem('bookingData');
    if (stored) {
      setBookingData(JSON.parse(stored));
    }
    setIsLoading(false);
  }, []);

  // Show loading state while checking for booking data
  if (isLoading) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading booking details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!bookingData) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <h4 className="text-muted mb-3">No booking data available</h4>
            <p className="text-muted mb-4">Please return to the booking page to continue.</p>
            <button 
              className="btn btn-primary" 
              onClick={() => navigate(`/booking/${eventName}`)}
            >
              Back to Booking
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { event, selectedSeats, totalCost } = bookingData;
  // const bookingFee = 2;
  const grandTotal = totalCost;

  const handleConfirmBooking = () => {
     

    const confirmed = {
      ...bookingData,
      // bookingFee,
      grandTotal,
      confirmedAt: new Date().toISOString(),
      eventId: bookingData.eventId,
    };

    setConfirmedBooking(confirmed);
    sessionStorage.setItem('confirmedBooking', JSON.stringify(confirmed));
    setShowPayment(true);
  };

  const handleBackToConfirm = () => {
    setShowPayment(false);
    setPaymentError('');
  };

  const handleBookingSuccess = () => {
    const modalEl = document.getElementById('successModal');
    if (modalEl) {
      const bsModal = bootstrap.Modal.getInstance(modalEl);
      if (bsModal) {
        bsModal.hide();
      }
    }

    setTimeout(() => {
      navigate('/my-bookings');
    }, 300);
  };

  const handleProceedToPayment = async () => {
    setIsProcessing(true);
  // const bookingFee = 2;
  const grandTotal = (totalCost)*100;

  const confirmed = {
    ...bookingData,
    // bookingFee,
    grandTotal,
    confirmedAt: new Date().toISOString(),
    eventId: bookingData.event._id || bookingData.event.id,
  };

  sessionStorage.setItem('confirmedBooking', JSON.stringify(confirmed));
  const seatId = JSON.parse(sessionStorage.getItem('selectedEvent'));
  const seatingArrangementId = seatId.seatingPlan;
  console.log(seatingArrangementId);
  const user = JSON.parse(localStorage.getItem('user'));
  console.log('User:', user);
  console.log('BookingData:', bookingData);
  console.log('Event ID from bookingData:', bookingData.eventId);
  
  try {
    let eventId = bookingData.eventId;
    
    // Fallback: try to get eventId from event object if not available directly
    if (!eventId && bookingData.event) {
      eventId = bookingData.event.id || bookingData.event._id;
      console.log('Using fallback eventId from event object:', eventId);
    }
    
    if (!eventId) {
      console.error('No eventId found in bookingData:', bookingData);
      console.error('Event object:', bookingData.event);
      throw new Error('Event ID is required for checkout');
    }
    
    const sessionData = {
      eventId: eventId,
      seatingArrangementId: seatingArrangementId,
      seats: selectedSeats.map(seat => ({
        ...seat,
        seatId: seat.id,
        id: undefined
      })).map(({ id, ...seat }) => seat), // Remove the old id field
      totalPrice: grandTotal,
      paymentStatus: 'pending',
      platformFeeAmount: 0,
      userId: user.id,
      paymentDetails: {
        successUrl: `${window.location.origin}/payment-success`,
        cancelUrl: `${window.location.origin}/payment-error`,
      }
    };

    console.log('Session data with eventId:', sessionData);

    const checkoutRes = await checkoutSession(sessionData);
    if (checkoutRes?.paymentResult?.url) {
      window.location.href = checkoutRes.paymentResult.url;
    } else {
      throw new Error('Stripe Checkout URL not returned');
    }
  } catch (err) {
    console.error('Payment Error:', err);
    alert('Something went wrong. Please try again.');
  } finally {
    setIsProcessing(false);
  }
};

  return (
    <AuthCheck redirectTo="/">
      <>
        <div className="container py-5">
          <div className="row">
            <div className="col-md-8 booking-info">
              {/* Back button */}
              <a 
                className="btn btn-link mb-4"
                onClick={() => {
                  if (showPayment) {
                    handleBackToConfirm();
                  } else {
                    // Navigate back to booking page with booking data preserved in sessionStorage
                    navigate(`/booking/${eventName}`);
                  }
                }} 
              >
                <svg className='pe-1' width={'1.125rem'} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                  <path d="M512 256A256 256 0 1 0 0 256a256 256 0 1 0 512 0zM215 127c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-71 71L392 232c13.3 0 24 10.7 24 24s-10.7 24-24 24l-214.1 0 71 71c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L103 273c-9.4-9.4-9.4-24.6 0-33.9L215 127z"/>
                </svg> 
                {showPayment ? 'Back to Booking Details' : 'Back to Booking'}
              </a>

              {/* Event Info */}
              <div className="d-flex mb-4">
                <img 
                  src={event.image} 
                  alt={event.title} 
                  style={{ width: '120px', height: 'auto' }} 
                  className="me-3 rounded" 
                />
                <div>
                  <h5>{event.title}</h5>
                  <p>
                    {/* {formatEventDate(event.datetime)}  */}
                    {/* <span className="mx-2">|</span>  */}
                    {event.venue?.name || 'N/A'}, {event.venue?.address || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Booking Info */}
              <div className="bg-light p-3 rounded">
                <h6>Booking Info</h6>
                {/* <ul className="list-unstyled mb-0">
                  {selectedSeats.map(seat => {
                    const ticketType = seat.ticketType || 'adult';
                    const seatPrice = ticketType === 'child' && seat.childprice !== undefined && seat.childprice !== null
                      ? seat.childprice
                      : seat.price || 0;
                    return (
                      <li key={seat.id}>
                         {seat.row}, Seat {seat.number} - {ticketType.charAt(0).toUpperCase() + ticketType.slice(1)} - £{seatPrice}
                      </li>
                    );
                  })}
                </ul> */}

  {/* <ul className="list-unstyled mb-0">
    {(() => {
      const counts = selectedSeats.reduce(
        (acc, seat) => {
          const type = seat.ticketType || 'adult';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        },
        {}
      );

      const totalSeats = selectedSeats.length;

      return (
        <li>
          <strong>{totalSeats}</strong> Seat{totalSeats > 1 ? 's' : ''} –{" "}
          {Object.entries(counts)
            .map(
              ([type, count]) =>
                `${count} ${type.charAt(0).toUpperCase() + type.slice(1)}${count > 1 ? 's' : ''}`
            )
            .join(", ")}
        </li>
      );
    })()}
  </ul> */}

<ul className="list-unstyled mb-0">
  
  {/* {(() => { */}
   {Object.entries(
    selectedSeats.reduce((acc, seat) => {
      // group key: row OR section
      const key = seat.row
        ? seat.row
        : seat.section || 'Unknown';

      if (!acc[key]) acc[key] = [];

      acc[key].push(seat);
      return acc;
    }, {})
  ).map(([seatKey, seats], index) => {
    const counts = selectedSeats.reduce((acc, seat) => {
      const type = seat.ticketType || 'adult';
      // acc[type] = (acc[type] || 0) + 1;
      acc[type] = new Set(seats.filter(s => (s.ticketType || 'adult') === type).map(s => s.id || `${s.row}-${s.number}`)).size;

      return acc;
    }, {});
// const seattype = selectedSeats[0].tableName;
//   const seatType = selectedSeats.tableName
//     ? selectedSeats[0].tableName           // round table
//     : `Row ${selectedSeats.tableName}`;
// const tableType = selectedSeats[0]?.tableType;
// const firstSeat = selectedSeats[0];
// let seattype = '';

// if (firstSeat?.row) {
//   seattype = ` ${firstSeat.row}`;
// } else if (firstSeat?.section) {
//   seattype = firstSeat.section;
// }

const totalSeats = new Set(seats.map(s => s.id || `${s.row}-${s.number}`)).size;

    // const totalSeats = selectedSeats.length;
// console.log('hii',selectedSeats[0]);

    return (
      <li key={index}>
        <strong> {seatKey}</strong> - {totalSeats} Seat{totalSeats > 1 ? 's' : ''} –{' '}
        {Object.entries(counts)
        .filter(([_, count]) => count > 0) 
          .map(([type, count]) => {
            let label;

            if (type === 'child') {
              label = count > 1 ? 'Children' : 'Child';
            } else if (type === 'adult') {
              label = count > 1 ? 'Adults' : 'Adult';
            }

            return `${count} ${label}`;
          })
          .join(', ')}
      </li>
    );
  })}
</ul>

                <div className="tickets-info mt-3">
                  <h6>Ticket Information</h6>
                  <p>1. Customer(s) can access their ticket(s) from the 'My Bookings' section on the app/mobile-web.</p>
                  <p>2. It is mandatory to present the ticket(s) in 'My Bookings' section via app/mobile-web at the venue.</p>	
                  <p>3. No physical ticket(s) are required to enter the venue.</p>
                </div>
              </div>

              {/* Payment Section - Only show after confirmation */}
              {showPayment && confirmedBooking && (
                !stripePromise ? (
                  <div className="alert alert-danger mt-4" role="alert">
                    <h6 className="alert-heading">Payment Configuration Error</h6>
                    <p className="mb-0">
                      Stripe payment is not properly configured. Please ensure that <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> is set in your environment variables.
                    </p>
                  </div>
                ) : (
                  <Elements stripe={stripePromise}>
                    <PaymentForm 
                      confirmedBooking={confirmedBooking}
                      eventName={eventName}
                      onSuccess={handleBookingSuccess}
                      onError={setPaymentError}
                    />
                  </Elements>
                )
              )}
            </div>

            <div className="col-md-4 order-summary mt-md-0 mt-4">
              <div className="border p-3 rounded">
                <h6 className="mb-3">Order Summary</h6>
                <div className="d-flex justify-content-between">
                  <span>Sub Total</span>
                  <span>£{totalCost}</span>
                </div>
                
                <hr />
                <div className="d-flex justify-content-between fw-bold">
                  <span>Order Total</span>
                  <span>£{totalCost}</span>
                </div>

               <p className='text-muted mt-4 small'>
                        <svg xmlns="http://www.w3.org/2000/svg" fill='#212529bf' className='pe-1' width={'1.125rem'} viewBox="0 0 512 512">
                          <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/>
                        </svg>
                        By proceeding, I express my consent to complete this transaction.
                      </p>
  <button
    className="w-100 btn btn-primary"
    onClick={handleProceedToPayment}
    disabled={isProcessing}
  >
    {isProcessing ? 'Processing...' : 'Proceed to Payment'}
  </button>


                {paymentError && (
                  <div className="alert alert-danger mt-3 small">
                    {paymentError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Success Modal */}
        <div
          className="modal fade"
          id="successModal"
          tabIndex="-1"
          aria-labelledby="successModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="successModalLabel">Booking Confirmed</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body">
                🎉 Your booking has been successfully confirmed!
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleBookingSuccess}
                >
                  View My Bookings
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    </AuthCheck>
  );
};

export default ConfirmBookingPage;